// Ops · Express monitor: pure display logic (sorting, countdowns, capacity, pause copy, the
// goodwill-credit form). No DOM, no Intl, so it runs under `node --test`.
// Contract: docs/PHASE6-CONTRACT.md (Ops · Express monitor) and the Phase 6 types in
// packages/core/src/v1/contract.d.ts (OpsExpress, OpsExpressRun, CreditView).
import { capacity } from '@popcart/core/v1';
import { count, countdown, peso } from '../../lib/format.js';

/** @typedef {import('@popcart/core/v1').OpsExpressRun} OpsExpressRun */
/** @typedef {import('@popcart/core/v1').OpsExpress} OpsExpress */
/** @typedef {'on_track' | 'at_risk' | 'late'} RunState */

/** The monitor polls every 10 s (contract: live runs; WebSocket events can replace it later). */
export const EXPRESS_POLL_MS = 10_000;

/** @type {Record<RunState, number>} */
const RANK = { late: 0, at_risk: 1, on_track: 2 };

/**
 * Late first, then at risk, then by minutes left (fewest first). Inside a state the run with the
 * least time (or the most minutes late) comes first; ties put unassigned/escalated runs first,
 * then the order code. The API already sorts this way; sorting again keeps the UI honest.
 * @template {Pick<OpsExpressRun, 'state' | 'minutesLeft' | 'code' | 'rider' | 'escalated'>} R
 * @param {readonly R[]} runs
 * @returns {R[]}
 */
export function sortRuns(runs) {
  return [...runs].sort(
    (a, b) =>
      (RANK[a.state] ?? 3) - (RANK[b.state] ?? 3) ||
      a.minutesLeft - b.minutesLeft ||
      Number(b.escalated) - Number(a.escalated) ||
      Number(a.rider !== null) - Number(b.rider !== null) ||
      a.code.localeCompare(b.code),
  );
}

/**
 * When the promise falls due, on this browser's clock. The server's `minutesLeft` is floored
 * whole minutes as of the response (`fetchedAt`), so the real deadline sits in
 * [fetchedAt + minutesLeft min, +1 min). When `promiseAt` agrees with that window (clocks within
 * about a minute) it gives the exact second; otherwise (a skewed clock) we anchor on
 * `minutesLeft` and take the middle of the minute.
 * @param {Pick<OpsExpressRun, 'promiseAt' | 'minutesLeft'>} run
 * @param {number} fetchedAt ms epoch of the response that carried the run
 */
export function deadlineAt(run, fetchedAt) {
  const anchored = fetchedAt + run.minutesLeft * 60_000;
  const exact = Date.parse(run.promiseAt);
  if (Number.isFinite(exact) && exact >= anchored - 60_000 && exact <= anchored + 120_000) return exact;
  return anchored + 30_000;
}

/**
 * The ticking countdown. Before the promise: "12:04" left; after it: "+3:10" late.
 * @param {number} msLeft
 * @returns {{ late: boolean; text: string; label: string }}
 */
export function countdownText(msLeft) {
  if (msLeft >= 0) {
    const t = countdown(msLeft);
    return { late: false, text: t, label: `${t} left` };
  }
  // Round the late side up to the second so 0.4 s late reads "+0:01", not "+0:00".
  const t = countdown(Math.ceil(-msLeft / 1000) * 1000);
  return { late: true, text: `+${t}`, label: `${t} late` };
}

/**
 * The state the pill shows: the server's state, but a run whose local countdown has crossed zero
 * reads late straight away instead of waiting for the next poll.
 * @param {RunState} state
 * @param {number} msLeft
 * @returns {RunState}
 */
export function liveState(state, msLeft) {
  return msLeft < 0 ? 'late' : state;
}

/** @type {Record<RunState, { tone: 'success' | 'warning' | 'error'; label: string }>} */
export const STATE_PILL = {
  on_track: { tone: 'success', label: 'On track' },
  at_risk: { tone: 'warning', label: 'At risk' },
  late: { tone: 'error', label: 'Late' },
};

/**
 * "Projected 6 min late", or null when the run is projected on time.
 * @param {Pick<OpsExpressRun, 'projectedLateMinutes'>} run
 */
export function projectedLabel(run) {
  const n = Math.round(run.projectedLateMinutes || 0);
  return n > 0 ? `Projected ${n} min late` : null;
}

/**
 * "Unassigned 2:14", ticking from the server's `unassignedSeconds` at the response time.
 * @param {number | null} unassignedSeconds
 * @param {number} fetchedAt
 * @param {number} now
 */
export function unassignedLabel(unassignedSeconds, fetchedAt, now) {
  if (unassignedSeconds === null || unassignedSeconds === undefined) return null;
  const ms = Math.max(0, unassignedSeconds * 1000 + Math.max(0, now - fetchedAt));
  return `Unassigned ${countdown(ms)}`;
}

/**
 * The capacity gauge. Uses the shared `capacity()` rule so the bar matches the API.
 * @param {{ active: number; max: number }} cap
 * @param {boolean} enabled
 * @returns {{ label: string; pct: number; full: boolean; tone: 'ok' | 'full' | 'off'; note: string }}
 */
export function capacityView(cap, enabled) {
  const c = capacity(Math.max(0, cap.active | 0), cap.max);
  const label = `${count(c.active)} of ${count(c.max)} Express runs`;
  if (!enabled) return { label, pct: c.pct, full: c.full, tone: 'off', note: 'Express is paused. Customers can’t choose it.' };
  if (c.max === 0) return { label, pct: 100, full: true, tone: 'off', note: 'No Express slots. Raise the limit to offer it.' };
  if (c.full) return { label, pct: 100, full: true, tone: 'full', note: 'Full. New customers see “Express is busy” until a run lands.' };
  const free = c.max - c.active;
  return { label, pct: c.pct, full: false, tone: 'ok', note: `${count(free)} ${free === 1 ? 'slot' : 'slots'} open` };
}

/**
 * Copy for the Pause / Resume confirm, and the settings patch it sends.
 * @param {boolean} enabled current state
 * @param {number} active Express runs on the road now
 */
export function pausePrompt(enabled, active) {
  if (enabled) {
    const runs = active === 1 ? 'The 1 run on the road keeps its promise.' : active > 1 ? `The ${count(active)} runs on the road keep their promise.` : 'No runs are on the road.';
    return {
      title: 'Pause Express?',
      body: `New customers won’t see Express until you resume it. ${runs}`,
      confirm: 'Pause Express',
      danger: true,
      patch: { enabled: false },
    };
  }
  return {
    title: 'Resume Express?',
    body: 'Customers can choose Express again right away.',
    confirm: 'Resume Express',
    danger: false,
    patch: { enabled: true },
  };
}

/** Express runs on the road at once (settings `express.maxActive`). */
export const MAX_ACTIVE = { min: 1, max: 100 };

/** @param {number} n */
export function clampMaxActive(n) {
  if (!Number.isFinite(n)) return MAX_ACTIVE.min;
  return Math.min(MAX_ACTIVE.max, Math.max(MAX_ACTIVE.min, Math.round(n)));
}

/**
 * Online riders with nothing on the road, for the Assign picker. Name order.
 * @template {{ online: boolean; activeDeliveries: number; name: string }} T
 * @param {readonly T[] | null | undefined} riders
 * @returns {T[]}
 */
export function freeRiders(riders) {
  return (riders ?? []).filter((r) => r.online && r.activeDeliveries === 0).sort((a, b) => a.name.localeCompare(b.name));
}

// ── Goodwill credit ─────────────────────────────────────────────────────────────────────────

/** Goodwill credits: ₱1 to ₱500 (the same ceiling as the late credit setting). */
export const CREDIT_LIMITS = { minCentavos: 100, maxCentavos: 50_000, noteMin: 3, noteMax: 200 };

/**
 * Pesos as typed → integer centavos. Accepts "50", "49.50", "₱1,000". Returns null when it isn't
 * a plain amount with at most two decimals.
 * @param {string} input
 * @returns {number | null}
 */
export function parsePesos(input) {
  const s = String(input ?? '').trim().replace(/^₱\s*/, '').replace(/,/g, '');
  if (!/^\d+(\.\d{1,2})?$/.test(s)) return null;
  const [whole, frac = ''] = s.split('.');
  return Number(whole) * 100 + Number(frac.padEnd(2, '0'));
}

/**
 * Validate the goodwill credit form.
 * @param {{ amount: string; note: string }} form
 * @returns {{ ok: true; value: { amountCentavos: number; note: string } } | { ok: false; errors: { amount?: string; note?: string } }}
 */
export function validateCredit(form) {
  /** @type {{ amount?: string; note?: string }} */
  const errors = {};
  const raw = String(form.amount ?? '').trim();
  const centavos = parsePesos(raw);
  if (!raw) errors.amount = 'Enter an amount in pesos.';
  else if (centavos === null) errors.amount = 'Pesos and centavos only, like 50 or 49.50';
  else if (centavos < CREDIT_LIMITS.minCentavos || centavos > CREDIT_LIMITS.maxCentavos)
    errors.amount = `Between ${peso(CREDIT_LIMITS.minCentavos)} and ${peso(CREDIT_LIMITS.maxCentavos)}.`;
  const note = String(form.note ?? '').trim();
  if (note.length < CREDIT_LIMITS.noteMin) errors.note = 'Add a short note: why this customer gets it.';
  else if (note.length > CREDIT_LIMITS.noteMax) errors.note = `Keep it under ${CREDIT_LIMITS.noteMax} characters.`;
  if (errors.amount || errors.note) return { ok: false, errors };
  return { ok: true, value: { amountCentavos: /** @type {number} */ (centavos), note } };
}

const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

/** "5 Nov" in local time. @param {string} iso */
export function shortDate(iso) {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '—';
  return `${d.getDate()} ${MONTHS[d.getMonth()]}`;
}

/**
 * The created credit in one line: "₱50 credit for PC-1042 · until 5 Nov".
 * @param {{ amountCentavos: number; expiresAt: string }} credit
 * @param {string | null} [orderCode]
 */
export function creditSummary(credit, orderCode) {
  return `${peso(credit.amountCentavos)} credit${orderCode ? ` for ${orderCode}` : ''} · until ${shortDate(credit.expiresAt)}`;
}
